import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Disc, Calendar, Tag, Music } from 'lucide-react';
import { BreadcrumbNav } from '../../components/layout/BreadcrumbNav.js';
import { SongCard } from '../../components/song/SongCard.js';
import { albumsService } from '../../services/albums.service.js';
import { songsService } from '../../services/songs.service.js';

function AlbumSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-4 w-48 bg-surface-hover rounded mb-6" />
      <div className="flex flex-col md:flex-row gap-6 mb-8">
        <div className="w-48 h-48 rounded-xl bg-surface-hover shrink-0" />
        <div className="space-y-3">
          <div className="h-8 w-56 bg-surface-hover rounded" />
          <div className="h-4 w-32 bg-surface-hover rounded" />
          <div className="h-4 w-24 bg-surface-hover rounded" />
        </div>
      </div>
      <div className="space-y-2">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="h-12 bg-surface-hover rounded-lg" />
        ))}
      </div>
    </div>
  );
}

export default function AlbumPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: albumData, isLoading: albumLoading } = useQuery({
    queryKey: ['album', slug],
    queryFn: () => albumsService.getAlbumBySlug(slug!),
    enabled: !!slug,
  });

  const album = albumData?.data;

  const { data: tracksData, isLoading: tracksLoading } = useQuery({
    queryKey: ['album', slug, 'tracks'],
    queryFn: () => songsService.getSongs({ albumSlug: slug, limit: 50 } as any),
    enabled: !!slug,
  });

  const tracks = tracksData?.data ?? [];

  if (albumLoading) {
    return <AlbumSkeleton />;
  }

  if (!album) {
    return (
      <div className="text-center py-16">
        <Disc size={64} className="mx-auto mb-4 text-text-muted opacity-50" />
        <h1 className="text-2xl font-bold text-text-primary mb-2">Album no encontrado</h1>
        <p className="text-text-secondary">El album que buscas no existe.</p>
        <Link to="/" className="text-primary hover:text-primary/80 mt-4 inline-block">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const breadcrumbs = [
    { label: album.artist.name, to: `/artists/${album.artist.slug}` },
    { label: album.title },
  ];

  return (
    <>
      <Helmet>
        <title>
          {album.title} - {album.artist.name} | LyricsHub
        </title>
        <meta
          name="description"
          content={`Letras del album ${album.title} de ${album.artist.name}. Tracklist completo en LyricsHub.`}
        />
      </Helmet>

      <BreadcrumbNav items={breadcrumbs} />

      {/* Album Header */}
      <div className="flex flex-col md:flex-row gap-6 mb-8">
        <div className="w-48 h-48 rounded-xl overflow-hidden bg-surface-hover shrink-0">
          {album.coverUrl ? (
            <img src={album.coverUrl} alt={album.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Disc size={48} className="text-text-muted" />
            </div>
          )}
        </div>

        <div className="flex flex-col justify-end">
          <p className="text-xs text-text-muted uppercase tracking-wide mb-1">Album</p>
          <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-2">{album.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-text-secondary">
            <Link
              to={`/artists/${album.artist.slug}`}
              className="font-medium hover:text-primary transition-colors"
            >
              {album.artist.name}
            </Link>
            {album.releaseYear && (
              <span className="flex items-center gap-1 text-text-muted">
                <Calendar size={14} />
                {album.releaseYear}
              </span>
            )}
            {album.genre && (
              <Link
                to={`/genres/${album.genre.slug}`}
                className="flex items-center gap-1 text-text-muted hover:text-primary transition-colors"
              >
                <Tag size={14} />
                {album.genre.name}
              </Link>
            )}
            <span className="flex items-center gap-1 text-text-muted">
              <Music size={14} />
              {album.songCount} canciones
            </span>
          </div>
        </div>
      </div>

      {/* Tracklist */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-text-primary text-sm">Tracklist</h2>
        </div>
        {tracksLoading ? (
          <div className="divide-y divide-border animate-pulse">
            {Array.from({ length: album.songCount || 8 }).map((_, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-4">
                <div className="w-6 h-4 bg-surface-hover rounded" />
                <div className="flex-1 space-y-1">
                  <div className="h-4 bg-surface-hover rounded w-1/2" />
                  <div className="h-3 bg-surface-hover rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : tracks.length === 0 ? (
          <p className="text-text-muted text-center py-8">No hay canciones disponibles.</p>
        ) : (
          <div className="divide-y divide-border">
            {tracks.map((track: any, i: number) => (
              <SongCard
                key={track.id}
                slug={track.slug}
                title={track.title}
                artistName={track.artistName}
                artistSlug={track.artistSlug}
                coverUrl={track.coverUrl}
                viewCount={track.viewCount}
                variant="list"
                index={i}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
